import { useEffect, useState, type ReactNode } from 'react';

import { Box, IconButton, Link, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

// How long the panel takes to slide, and so how long to wait before putting a
// closed drawer back to its own width.
const SLIDE_MS = 220;

export interface DrawerProps {
  open: boolean;
  title: string;
  /** Body copy under the title. */
  subtitle?: ReactNode;
  onClose: () => void;
  /** Pinned action row below the scrolling body (e.g. a Continue button). */
  footer?: ReactNode;
  /**
   * Offers a link in the header band that takes the panel to the full frame.
   *
   * For a drawer holding more than a drawer's worth. Attendance Allowance is
   * 227 questions, and at 440px a question and its answer stack and wrap, so
   * the list is several times longer than it needs to be. Widening it is what
   * shortens it.
   *
   * Off by default: a carer's six facts have nothing to gain from the whole
   * frame. The choice is per drawer and sticks while the session lasts, so a
   * care expert who works full screen is not asked again each time.
   */
  expandable?: boolean;
  children: ReactNode;
}

/**
 * The right-anchored task drawer: a Floral White header band (title + subtitle
 * + close) over a Gardenia scrolling body, with an optional pinned footer. An
 * in-tree overlay — a portal Drawer would escape the scaled laptop frame (see
 * CamShell) — so render it through CamShell's / ControlPanel's `overlays` slot.
 * Owns the overlay mechanics and the 20px band padding so call sites just pass
 * content. Shared by Schedule cover and Create action.
 */
const Drawer = ({
  open,
  title,
  subtitle,
  onClose,
  footer,
  expandable = false,
  children,
}: DrawerProps) => {
  const [expanded, setExpanded] = useState(false);

  // A drawer is a drawer every time it opens. Full screen is a thing you do to
  // the panel in front of you, not a preference the next one inherits.
  //
  // Reset after the slide rather than on close, or the panel narrows on its way
  // out.
  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setExpanded(false), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <Box
      aria-hidden={!open}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {/* backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'custom.charcoal',
          opacity: open ? 0.4 : 0,
          transition: 'opacity 200ms ease',
        }}
      />

      {/* sliding panel */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: expanded ? '100%' : 440,
          maxWidth: expanded ? '100%' : '90%',
          transition: `transform ${SLIDE_MS}ms ease, width ${SLIDE_MS}ms ease`,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          borderLeft: 1,
          borderColor: 'divider',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        <Box sx={{ p: 4, bgcolor: 'background.paper' }}>
          <Stack
            sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
            direction='row'
            spacing={2}
          >
            {/* h6. Every drawer in the app is a CAM one, opening over a
              workspace whose card headers are h6, and it took two passes to
              accept that the panel does not need to be louder than the thing
              it opened over: it is already the only thing on top. Marine Green
              is what separates it, not size.

              It started at h3, a 20px Lora italic line over 14px cards, which
              announced itself like a page title rather than naming a panel.

              The subtitle stays body2, so the two lines still read as a heading
              and a line under it rather than one block. */}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='h6' color='primary.main'>
                {title}
              </Typography>
              {subtitle &&
                (typeof subtitle === 'string' ? (
                  <Typography
                    sx={{ mt: 0.5 }}
                    variant='body2'
                    color='text.secondary'
                  >
                    {subtitle}
                  </Typography>
                ) : (
                  <Box sx={{ mt: 0.5 }}>{subtitle}</Box>
                ))}
            </Box>
            <Stack
              sx={{ alignItems: 'center', flexShrink: 0 }}
              direction='row'
              spacing={1.5}
            >
              {/* A link rather than a button: it changes how you are looking at
                the panel, which is not one of the things the panel does. */}
              {expandable && (
                <Link
                  component='button'
                  type='button'
                  variant='body2'
                  onClick={() => setExpanded((e) => !e)}
                >
                  {expanded ? 'Exit full screen' : 'Full screen'}
                </Link>
              )}
              <IconButton aria-label='close' onClick={onClose}>
                <Icon icon='xmark' fontSize={18} />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        {/* body scrolls; the footer stays pinned below */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 4 }}>
          {children}
        </Box>

        {footer && (
          <Box
            sx={{
              p: 4,
              pt: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              // Full width in a 440px panel, hugging its content across the
              // whole frame: an action a laptop wide stops reading as a button.
              // The slot decides, so a call site passes a plain button and does
              // not have to know how wide the panel currently is.
              '& > *': { width: expanded ? 'auto' : '100%' },
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Box>
  );
};

Drawer.displayName = 'Drawer';
export default Drawer;
